import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPackage, 
  FiShoppingBag, 
  FiChevronLeft, 
  FiChevronRight, 
  FiChevronsLeft, 
  FiChevronsRight,
  FiSearch,
  FiX
} from 'react-icons/fi';
import FilterTab from '../components/FilterTab';
import ProductCard from '../components/ProductCard';
import SortDropdown from '../components/SortDropdown';
import FilterSidebar from '../components/FilterSidebar';
import FilterButton from '../components/FilterButton';
import { toast } from 'react-hot-toast';
import { userApi } from '../../api/userApi';

const filterOptions = {
  categories: [], // Will be populated from API
  sizes: [], // Will be populated from API
  colors: [], // Will be populated from API
  brands: [], // Will be populated from API
  materials: ['Silk', 'Cotton', 'Chiffon', 'Georgette', 'Velvet'],
  occasions: ['Wedding', 'Party', 'Casual', 'Festival', 'Office'],
  availability: ['In Stock', 'Out of Stock'],
  ratings: ['4★ & above', '3★ & above', '2★ & above'],
  discounts: ['10% & above', '20% & above', '30% & above', '50% & above'],
  priceRange: {
    min: 0,
    max: 100000,
    step: 1000
  }
};

const ITEMS_PER_PAGE = 12; // 3 rows × 4 columns

const getPaginationRange = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  if (current <= 3) return [1, 2, 3, 4, '...', total - 1, total];
  if (current >= total - 2) return [1, 2, '...', total - 3, total - 2, total - 1, total];

  return [
    1,
    '...',
    current - 1,
    current,
    current + 1,
    '...',
    total
  ];
};

const Products = () => {
  // 1. First get URL params and navigation
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const filterType = searchParams.get('filter') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const categoryParam = searchParams.get('category'); // Add this line
  const brandParam = searchParams.get('brand'); // Add this line

  // 2. State declarations
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchQuery);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(filterType);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState({
    categories: [], // Will be populated from API
    sizes: [], // Will be populated from API
    colors: [], // Will be populated from API
    brands: [], // Will be populated from API
    materials: ['Silk', 'Cotton', 'Chiffon', 'Georgette', 'Velvet'],
    occasions: ['Wedding', 'Party', 'Casual', 'Festival', 'Office'],
    availability: ['In Stock', 'Out of Stock'],
    ratings: ['4★ & above', '3★ & above', '2★ & above'],
    discounts: ['10% & above', '20% & above', '30% & above', '50% & above'],
    priceRange: {
      min: 0,
      max: 100000,
      step: 1000
    }
  });
  const [selectedFilters, setSelectedFilters] = useState({
    categories: ['All'],
    sizes: ['All'],
    colors: ['All'],
    brands: ['All'],
    materials: ['All'],
    occasions: ['All'],
    availability: ['All'],
    ratings: ['All'],
    discounts: ['All'],
    priceRange: [0, 100000]
  });
  const [bannerFilter, setBannerFilter] = useState(() => {
    const brand = searchParams.get('brand');
    const category = searchParams.get('category');
    if (brand) return { type: 'brand', value: brand };
    if (category) return { type: 'category', value: category };
    return null;
  });
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    // Set page as loaded after initial render
    setPageLoaded(true);
    return () => setPageLoaded(false);
  }, []);

  // Update useEffect to set initial category filter
  useEffect(() => {
    if (categoryParam) {
      setSelectedFilters(prev => ({
        ...prev,
        categories: [categoryParam]
      }));
    }
  }, [categoryParam]);

  // Update useEffect to set initial brand filter
  useEffect(() => {
    if (brandParam) {
      setSelectedFilters(prev => ({
        ...prev,
        brands: [brandParam]
      }));
    }
  }, [brandParam]);

  // 3. Define applyFilters function using useCallback
  const applyFilters = useCallback(() => {
    if (!products.length) return;

    let filtered = [...products];

    // Apply brand filter from URL param or selected filters
    if (brandParam || !selectedFilters.brands.includes('All')) {
      const brandsToFilter = brandParam 
        ? [brandParam] 
        : selectedFilters.brands;
      filtered = filtered.filter(product => 
        brandsToFilter.includes(product.brand)
      );
    }

    // Apply category filter
    if (categoryParam || !selectedFilters.categories.includes('All')) {
      const categoriesToFilter = categoryParam 
        ? [categoryParam] 
        : selectedFilters.categories;
      filtered = filtered.filter(product => 
        categoriesToFilter.includes(product.category)
      );
    }

    // Apply collection/tag filter
    if (filterType && filterType !== 'all') {
      filtered = filtered.filter(product => 
        product.tags.some(tag => tag.toLowerCase() === filterType.toLowerCase())
      );
    }

    // Apply size filter
    if (!selectedFilters.sizes.includes('All')) {
      filtered = filtered.filter(product =>
        product.variants?.some(variant => 
          selectedFilters.sizes.includes(variant.size)
        )
      );
    }

    // Apply color filter
    if (!selectedFilters.colors.includes('All')) {
      filtered = filtered.filter(product =>
        product.variants?.some(variant => 
          selectedFilters.colors.includes(variant.color)
        )
      );
    }

    // Apply brand filter
    if (!selectedFilters.brands.includes('All')) {
      filtered = filtered.filter(product =>
        selectedFilters.brands.includes(product.brand)
      );
    }

    // Apply availability filter
    if (!selectedFilters.availability.includes('All')) {
      filtered = filtered.filter(product => {
        if (selectedFilters.availability.includes('In Stock')) {
          return product.stock > 0;
        }
        if (selectedFilters.availability.includes('Out of Stock')) {
          return product.stock === 0;
        }
        return true;
      });
    }

    // Apply price range filter
    filtered = filtered.filter(product => {
      const price = parseFloat(product.salePrice || product.price);
      return price >= selectedFilters.priceRange[0] && 
             price <= selectedFilters.priceRange[1];
    });

    // Apply search filter
    if (currentSearchTerm) {
      const searchLower = currentSearchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.category.toLowerCase().includes(searchLower) ||
        product.brand.toLowerCase().includes(searchLower) ||
        product.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
        break;
      case 'price-low-high':
        filtered.sort((a, b) => 
          (parseFloat(a.salePrice || a.price)) - (parseFloat(b.salePrice || b.price))
        );
        break;
      case 'price-high-low':
        filtered.sort((a, b) => 
          (parseFloat(b.salePrice || b.price)) - (parseFloat(a.salePrice || a.price))
        );
        break;
    }

    setFilteredProducts(filtered);
  }, [products, selectedFilters, sortBy, currentSearchTerm, categoryParam, brandParam, filterType]);

  // 4. Transform API data function
  const transformProductData = (apiProduct) => ({
    id: apiProduct.id,
    name: apiProduct.name,
    price: parseFloat(apiProduct.price),
    salePrice: parseFloat(apiProduct.sale_price),
    finalPrice: parseFloat(apiProduct.final_price),
    category: apiProduct.category.name,
    brand: apiProduct.brand.name,
    activeOffers: apiProduct.active_offers,
    variants: apiProduct.variants,
    images: apiProduct.variants.reduce((acc, variant) => {
      const variantImages = variant.images.map(img => ({
        url: img.images,
        alt: `${apiProduct.name} - ${variant.color}`
      }));
      return [...acc, ...variantImages];
    }, []),
    tags: apiProduct.tags.map(tag => tag.name),
    stock: apiProduct.variants.reduce((total, variant) => total + variant.stock, 0),
    description: apiProduct.description,
    isActive: apiProduct.is_active,
    highlights: apiProduct.highlights,
    specifications: apiProduct.specifications,
    stylingTips: apiProduct.styling_tips,
    isNewIn: apiProduct.tags.some(tag => tag.name.toLowerCase() === 'new'),
    isOffer: apiProduct.active_offers.length > 0,
    dateAdded: apiProduct.created_at
  });

  // 5. Fetch products function
  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await userApi.getLatestProducts();
      const transformedProducts = Array.isArray(data) 
        ? data.map(transformProductData)
        : [transformProductData(data)];

      setProducts(transformedProducts);
      
      let filtered = [...transformedProducts];

      // Apply brand filter if present
      if (brandParam) {
        filtered = filtered.filter(product => product.brand === brandParam);
      }

      // Apply category filter if present
      if (categoryParam) {
        filtered = filtered.filter(product => product.category === categoryParam);
      }

      // Apply collection/tag filter if present
      if (filterType && filterType !== 'all') {
        filtered = filtered.filter(product => 
          product.tags.some(tag => tag.toLowerCase() === filterType.toLowerCase())
        );
      }

      setFilteredProducts(filtered);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products');
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  // 6. Effects
  useEffect(() => {
    fetchProducts();
  }, [currentSearchTerm, filterType, categoryParam, brandParam]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    if (products.length > 0) {
      const categories = [...new Set(products.map(p => p.category))];
      const brands = [...new Set(products.map(p => p.brand))];
      const sizes = [...new Set(products.flatMap(p => 
        p.variants?.map(v => v.size) || []
      ))];
      const colors = [...new Set(products.flatMap(p => 
        p.variants?.map(v => v.color) || []
      ))];

      setFilterOptions(prev => ({
        ...prev,
        categories,
        brands,
        sizes,
        colors
      }));
    }
  }, [products]);

  // Add error boundary to prevent infinite renders
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;

    const fetchWithRetry = async () => {
      try {
        await fetchProducts();
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchWithRetry, 1000 * retryCount); // Exponential backoff
        }
      }
    };

    fetchWithRetry();
  }, [searchParams]);

  // Separate the banner filter logic from fetchProducts
  useEffect(() => {
    const brand = searchParams.get('brand');
    const category = searchParams.get('category');
    
    if (brand) {
      setBannerFilter({ type: 'brand', value: brand });
    } else if (category) {
      setBannerFilter({ type: 'category', value: category });
    } else if (bannerFilter) {
      setBannerFilter(null);
    }
  }, [searchParams]);

  // 7. Helper functions
  const getPaginationRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, '...', total];
    if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  const filterProducts = (products, filter) => {
    setActiveFilter(filter);
    let filtered = [...products];

    switch (filter) {
      case 'new-in':
        filtered = products.filter(product => product.isNewIn);
        break;
      case 'bridal':
        filtered = products.filter(product => 
          product.tags.includes('bridal') || 
          product.category === 'Bridal Collection'
        );
        break;
      case 'traditional':
        filtered = products.filter(product => 
          product.tags.includes('traditional') || 
          product.category === 'Traditional Wear'
        );
        break;
      case 'offers':
        filtered = products.filter(product => product.isOffer || product.salePrice);
        break;
      case 'trending':
        filtered = products.filter(product => product.tags.includes('trending'));
        break;
      default:
        // No specific filter applied
        break;
    }

    // Apply category filter if present
    if (!selectedFilters.categories.includes('All')) {
      filtered = filtered.filter(product => 
        selectedFilters.categories.includes(product.category)
      );
    }

    setFilteredProducts(filtered);
  };

  const handleFilterClick = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    
    // Count non-'All' selections for each filter category
    Object.entries(selectedFilters).forEach(([key, values]) => {
      if (key === 'priceRange') {
        // Count price range if it's not at default values
        if (values[0] !== 0 || values[1] !== 100000) {
          count++;
        }
      } else {
        // Count if any non-'All' value is selected
        if (!values.includes('All') && values.length > 0) {
          count++;
        }
      }
    });

    return count;
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      categories: ['All'],
      sizes: ['All'],
      colors: ['All'],
      brands: ['All'],
      materials: ['All'],
      occasions: ['All'],
      availability: ['All'],
      ratings: ['All'],
      discounts: ['All'],
      priceRange: [0, 100000]
    });
    setCurrentPage(1); // Reset to first page
    setSortBy('newest'); // Reset sorting to default
  };

  const renderSearchHeader = () => {
    if (!currentSearchTerm) return null;

    return (
      <div className="mb-6 flex items-center justify-between bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center">
          <FiSearch className="h-5 w-5 text-gray-400 mr-2" />
          <span className="text-gray-600">
            Search results for: <span className="font-medium">{currentSearchTerm}</span>
          </span>
        </div>
        <button
          onClick={() => {
            setCurrentSearchTerm('');
            navigate('/products');
          }}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear search
        </button>
      </div>
    );
  };

  const clearBannerFilter = useCallback(() => {
    setBannerFilter(null);
    navigate('/products');
  }, [navigate]);

  const renderBannerFilter = () => {
    if (!bannerFilter) return null;

    return (
      <div className="bg-gray-100 p-4 mb-8 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {bannerFilter.type === 'brand' ? 'Brand:' : 'Category:'} {bannerFilter.value}
            </h2>
            <p className="text-sm text-gray-600">
              Showing all products from {bannerFilter.type === 'brand' ? 'brand' : 'category'} "{bannerFilter.value}"
            </p>
          </div>
          <button
            onClick={clearBannerFilter}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            Clear filter <FiX className="ml-1" />
          </button>
        </div>
      </div>
    );
  };

  const renderCollectionBanner = () => {
    if (!filterType || filterType === 'all') return null;

    return (
      <div className="bg-gray-100 p-4 mb-8 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Collection: {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            </h2>
            <p className="text-sm text-gray-600">
              Showing all products from collection "{filterType}"
            </p>
          </div>
          <button
            onClick={() => {
              navigate('/products');
              setActiveFilter('all');
            }}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            Clear filter <FiX className="ml-1" />
          </button>
        </div>
      </div>
    );
  };

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={fetchProducts}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Products | Panchali Boutique</title>
      </Helmet>

      <div className="min-h-screen bg-[#FAF9F6]">
        <div className="relative max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {renderSearchHeader()}
          {renderBannerFilter()}
          {renderCollectionBanner()}
          {/* Filters and Sort Section */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4 w-full sm:w-auto justify-between">
              <FilterButton 
                onClick={() => setShowFilters(true)} 
                showFilters={showFilters}
                activeFiltersCount={getActiveFiltersCount()}
              />
              <div className="sm:hidden">
                <SortDropdown sortBy={sortBy} setSortBy={setSortBy} />
              </div>
            </div>
            <div className="hidden sm:block">
              <SortDropdown sortBy={sortBy} setSortBy={setSortBy} />
            </div>
          </div>

          {/* Products Grid or Empty State */}
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
              {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                <div key={index} className="bg-gray-100 rounded-xl aspect-w-3 aspect-h-4 animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
              <AnimatePresence mode="sync">
                {currentProducts.map((product) => (
                  <ProductCard 
                    key={product.id} 
                    product={product}
                    className="rounded-none"
                  />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 px-4"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <FiPackage className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-medium text-gray-900 mb-2">
                No products found
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {selectedFilters.brands.includes('All') 
                  ? "We couldn't find any products. Please check back later."
                  : "We couldn't find any products matching your filters. Try adjusting your selection."}
              </p>
              <div className="flex items-center justify-center space-x-4">
                {!selectedFilters.brands.includes('All') && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center justify-center w-48 h-[50px] border border-gray-300 shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Clear all filters
                  </button>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center justify-center w-48 h-[50px] bg-[#2C3E50] text-white hover:bg-[#34495E] transition-colors"
                >
                  <FiShoppingBag className="mr-2 h-5 w-5" />
                  Continue Shopping
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          {/* Previous Page */}
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg transition-colors ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FiChevronLeft className="w-5 h-5" />
          </button>

          {/* Page Numbers */}
          {getPaginationRange(currentPage, totalPages).map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-4 py-2">...</span>
              ) : (
                <button
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-[#2C3E50] text-white hover:bg-[#34495E]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}

          {/* Next Page */}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg transition-colors ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FiChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        filterOptions={filterOptions}
        applyFilters={applyFilters}
        clearAllFilters={clearAllFilters}
      />
    </>
  );
};

export default Products;






































